import type { QueryKey, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../../app/api';
import type { PanelSettingsDto, SettingsUpdateDto, UpdateSettingsResponseDto } from '../../../app/api/generated/client';
import { showToast } from '../../../app/utils/toastUtils';
import { mutationKeys } from '../mutation-keys';
import { queryKeys } from '../query-keys';

// Whether the toggle affects the whole panel or a single position/cashflow row.
type SettingsScope = 'panel' | 'item';

type Context = { previousSettings?: PanelSettingsDto };

interface Options {
    mutationFn: (payload: SettingsUpdateDto) => Promise<UpdateSettingsResponseDto>;
    settingsQueryKey: (partnerUuid?: string) => QueryKey;
    scope: SettingsScope;
}

/**
 * Shared optimistic-update wiring for every liquidity settings toggle (panel on/off + per-item checkboxes).
 *
 * `onMutate` patches the `enabled` flag straight into the settings cache so the control reacts instantly,
 * `onError` rolls back, and
 * `onSuccess` lets the server stay the source of truth by refetching the settings plus the server-computed calculation
 * and overview.
 *
 * All toggles share a single `mutationKey` so `useLiquidityCalculationData` can dim the table/graph while any of them is in flight.
 */
export const useLiquiditySettingsMutation = ({
    mutationFn,
    settingsQueryKey,
    scope,
}: Options): UseMutationResult<UpdateSettingsResponseDto, AxiosError, SettingsUpdateDto, Context> => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: client } = useClient();
    const partnerUuid = client?.partnerUuid;
    const settingsKey = settingsQueryKey(partnerUuid);

    return useMutation({
        mutationKey: mutationKeys.updateLiquiditySettings(),
        mutationFn,
        onMutate: async ({ id, enabled }) => {
            await queryClient.cancelQueries({ queryKey: settingsKey });
            const previousSettings = queryClient.getQueryData<PanelSettingsDto>(settingsKey);

            if (previousSettings) {
                const nextSettings: PanelSettingsDto = {
                    ...previousSettings,
                    ...(scope === 'panel'
                        ? { enabled }
                        : { itemsSettings: previousSettings.itemsSettings.map((item) => (item.id === id ? { ...item, enabled } : item)) }),
                };
                queryClient.setQueryData<PanelSettingsDto>(settingsKey, nextSettings);
            }

            return { previousSettings };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousSettings) queryClient.setQueryData(settingsKey, context.previousSettings);
            showToast('error', true, t('liquidityCalculatorSettings.updateError'));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: settingsKey });
            queryClient.invalidateQueries({ queryKey: queryKeys.liquidityOverview(partnerUuid) });
            queryClient.invalidateQueries({ queryKey: queryKeys.liquidityCalculatorCalculation(partnerUuid) });
        },
    });
};
